<template>
  <div class="mt-4 b-t b-grey-70 pt-4 px-4 lg:px-6">
    <define-store-address>
      <span>
        {{ store.address1 }}<br>
        <template v-if="store.address2">{{ store.address2 }}<br></template>
        {{ store.city }} {{ store.state }}. {{ store.postalcode }}, {{ store.country }}<br>
      </span>
    </define-store-address>
    <div class="w-full gap-x-4 align-top i-flex">
      <base-button
        v-if="!disableSelection"
        :aria-label="store.name"
        :aria-pressed="store.storeid === selectedStore?.storeid"
        class="h-6 w-6 shrink-0 rounded-full p-1 ring ring-grey-40 ring-inset duration peer-focus-visible:outline-auto peer-focus-within:ring-grey-10 !mt-1 !h-4 !w-4"
        @click="selectedStore = store"
      >
        <span
          class="block full rounded-full duration bg-brand-1"
          :class="store.storeid === selectedStore?.storeid ? 'op-100 ' : 'op-0'"
        />
      </base-button>
      <div class="w-full text-base c-grey-10">
        <base-collapse class-toggle="w-full">
          <template #toggle="{ expanded }">
            <span class="flex between">
              <span class="subtitle-4">{{ store.name }}</span>
              <vf-icon :dir="expanded ? 'up' : 'down'" name="chevron" size="md" />
            </span>
            <span class="flex text-sm between c-grey-20">
              <slot name="store-body-collapsed" :store>
                <store-address v-if="showAddressPreview" class="mt-2 " />
                <template v-else>
                  <span v-if="qty && store.bopis_enabled === '1'">{{ $t.readyIn24hours }}.</span>
                  <span v-else-if="store.sts_enabled === '1'">{{ $t.readyIn3Days }}.</span>
                </template>
                <span>({{ store._distance }} {{ store._distanceuom }})</span>
              </slot>
            </span>
            <span v-if="qty && qty < qtyThreshold" class="text-sm c-grey-20">
              {{ replaceAll($t.qtyLimitLow, { limit: qty }) }}
            </span>
          </template>
          <div class="pt-4 space-y-6">
            <address class="text-sm">
              <store-address v-if="!showAddressPreview"  />
              <span class="mt-4 flex items-center gap-2">
                <vf-icon name="location" size="lg" />
                <vf-link :to="`http://maps.google.com/?q=${store.latitude},${store.longitude}`">
                  {{ $t.getDirections }}
                </vf-link>
              </span>
              <span class="mt-4 flex items-center gap-2">
                <vf-icon name="phone" size="lg" />
                <vf-link :to="`tel:${store.phone}`">
                  {{ store.phone }}
                </vf-link>
              </span>
            </address>
            <div>
              <div class="mb-2 fw-medium">
                {{ $t.storeHours }}
              </div>
              <ul >
                <li>{{ $t.weekday }}: {{ store.m }}</li>
                <li>{{ $t.saturday }}: {{ store.sa }}</li>
                <li>{{ $t.sunday }}: {{ store.su }}</li>
              </ul>
            </div>
            <div v-if="store.store_description">
              <div class="mb-2 fw-medium">
                {{ $t.getStoreInstruction }}
              </div>
              <span >
                {{ store.store_description }}
              </span>
            </div>
            <div v-if="$feature.allowProductReservationOnPDP && qty">
              <vf-button
                v-if="!isReservationOpen"
                class="mt-6"
                :loading="isReservationOutgoing"
                @click="handleReserve"
              >
                {{ $t.reserve }}
              </vf-button>
              <base-collapse
                v-model="isReservationOpen"
                class="mt-6 b-grey-70 lg:col-span-6"
                class-toggle="hidden"
                data-test-id="store-reservation-collapse"
                style="border: 0"
              >
                <form-robis
                  :cart-id="cartId"
                  :store-shipping-address="storeShippingAddress"
                  @success="isReservationOpen = false"
                />
              </base-collapse>
            </div>
          </div>
        </base-collapse>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { LocatorSearchItem } from '#types/brandify'
import type { PickupProduct } from '#types/components/product/pickupInStore'

const props = defineProps<{
  item?: PickupProduct
  store: LocatorSearchItem & { storeInventory?: number }
  disableSelection?: boolean
  showAddressPreview?: boolean
}>()

const emit = defineEmits<{
  error: [value: boolean]
}>()

const [DefineStoreAddress, StoreAddress] = createReusableTemplate()

const selectedStore = defineModel<LocatorSearchItem>()

const { $feature } = useNuxtApp()
const { qtyThreshold } = useAppConfig().components.form.bopisStoreSearch
const qty = computed(() => props.store.quantity || props.store.storeInventory)

let cartId: ReturnType<typeof useProductReservation>['cartId']
let isReservationOpen: ReturnType<typeof useProductReservation>['isReservationOpen']
let isReservationOutgoing: ReturnType<typeof useProductReservation>['isReservationOutgoing']
let reserve: ReturnType<typeof useProductReservation>['reserve']
let storeShippingAddress: ReturnType<typeof useProductReservation>['storeShippingAddress']

if (props.item && $feature.allowProductReservationOnPDP) {
  ({ cartId, isReservationOpen, isReservationOutgoing, reserve, storeShippingAddress } = useProductReservation({
    item: props.item,
    store: props.store
  }))
}

const handleReserve = () => {
  try {
    reserve?.()
  }
  catch (e) {
    emit('error', true)
  }
}
</script>
